import { NetScoreFormat } from '@upright/common/model/output/scores/types';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ModelEntityType } from '../../../../../../../../../../common/model/input/types';
import { EQUAL_WEIGHTS_TITLE } from '../../../../../../../../../../common/model/input/value-sets';
import { getValueSetName } from '../../../../../../../../../untidy/redux/selectors';
import { friendlyInSentenceModelEntityType } from '../../../../../../../../../untidy/texts';
import { getTextStyle } from '../../../../../../../../generic/content/texts/bou-text/text-style';
import { TooltipParagraph } from '../../../../../../../../generic/content/texts/tooltip-paragraph';
import { BouColor } from '../../../../../../../../generic/global/bou-colors';

interface Props {
  netScoreFormat: NetScoreFormat;

  type?: ModelEntityType;
  absoluteValueFormatter?: (value: number) => string;
  valueSetName?: string;
}

export function NetScoreExplanation(props: Props) {
  const selectedValueSetName = useSelector(getValueSetName);
  const valueSetName = props.valueSetName || selectedValueSetName;
  switch (props.netScoreFormat) {
    case NetScoreFormat.NET_RATIO:
    case NetScoreFormat.NET_RATIO_DECIMAL: {
      return (
        <Root>
          <TooltipParagraph>
            <span>
              Net impact ratio represents the net impact of a{' '}
              {props.type ? (
                friendlyInSentenceModelEntityType(props.type)
              ) : (
                <>product, company, groups of companies or portfolio</>
              )}
              . It is defined as (<Positive>positive impacts</Positive> - <Negative>negative impacts</Negative>) /{' '}
              <Positive>positive impacts</Positive>.
              <br />
              <br />
              The maximum value for net impact ratio is 100 %, representing a theoretical company with no negative
              impacts. The minimum value is -∞.
            </span>
          </TooltipParagraph>
          {getValueSetParagraph(valueSetName)}
        </Root>
      );
    }
    case NetScoreFormat.NET_SUM: {
      return (
        <Root>
          <TooltipParagraph>
            {props.type ? (
              <span>
                The <b>net score</b> summarizes the net impact of this {friendlyInSentenceModelEntityType(props.type)},
                based on a rough estimate prepared by Upright's AI Leena-Leena.
              </span>
            ) : (
              <span>
                The <b>net score</b> summarizes the net impact of a product, company, groups of companies or portfolio,
                based on a rough estimate prepared by Upright's AI Leena-Leena.
              </span>
            )}
          </TooltipParagraph>
          {getValueSetParagraph(valueSetName)}
        </Root>
      );
    }
  }
}

function getValueSetParagraph(valueSetName: string) {
  switch (valueSetName) {
    case EQUAL_WEIGHTS_TITLE:
      return (
        <TooltipParagraph>
          Net scores depend on values: the shown score has been calculated in Upright's equal weights value set, meaning
          that all 19 impacts in the Upright net model are considered equally important for the final score.
        </TooltipParagraph>
      );
    case 'custom':
    case 'Custom':
      return (
        <TooltipParagraph>Net scores depend on values: the score is shown in a custom value set.</TooltipParagraph>
      );
    default:
      return (
        <TooltipParagraph>
          Net scores depend on values: the score is shown in the <b>{valueSetName}</b> value set.
        </TooltipParagraph>
      );
  }
}

const Positive = styled.span`
  color: ${BouColor.IMPACT_POSITIVE_GREEN};
  filter: brightness(85%);
  font-weight: bold;
`;
const Negative = styled.span`
  color: ${BouColor.IMPACT_NEGATIVE_PURPLE};
  font-weight: bold;
`;

const Root = styled.div`
  ${getTextStyle({ size: 'xxs' })}
  text-align: left;
  line-height: 1.45;
  font-weight: normal;
`;
